'use client';

/**
 * HandoffTimeoutMessage — "leave a message" sub-view of the timeout
 * recovery flow (see handoff-timeout-panel.tsx for the view machine this
 * composes into).
 *
 * Owns its OWN textarea state locally — the container only ever needs the
 * final trimmed body on submit. That's deliberate: a failed request leaves
 * this component mounted (the container's view stays MESSAGE, only its
 * `error` prop changes), so the user's typed draft survives a retry for
 * free, without any extra plumbing to "preserve" it.
 */
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StandardCard } from './primitives/standard-card';
import { SpeechBubbleIcon } from './primitives/handoff-icons';

interface HandoffTimeoutMessageProps {
  /** Email on file from the pre-handoff contact form, resolved by the
   *  container from the CONTACT_SUMMARY message tied to this handoff (see
   *  handoff-timeout-panel.tsx). Undefined only if that row can't be found
   *  (e.g. an unusual session) — falls back to generic copy rather than
   *  fabricating an address. */
  email?: string;
  onBack: () => void;
  onSubmit: (body: string) => void;
  submitting: boolean;
  error?: string | null;
}

export function HandoffTimeoutMessage({
  email,
  onBack,
  onSubmit,
  submitting,
  error,
}: HandoffTimeoutMessageProps) {
  const [value, setValue] = useState('');
  const canSubmit = value.trim().length > 0 && !submitting;

  return (
    <StandardCard
      icon={<SpeechBubbleIcon />}
      eyebrow="LEAVE A MESSAGE"
      title="Send the team a message"
    >
      <div className="flex flex-col gap-[12px]">
        <div className="text-[13.5px]" style={{ color: '#8a7c68' }}>
          We&apos;ll reply to{' '}
          {email ? (
            <span className="font-semibold" style={{ color: '#31364E' }}>
              {email}
            </span>
          ) : (
            'the email you shared earlier'
          )}{' '}
          — usually within a few hours. Your earlier chat is attached
          automatically.
        </div>

        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitting}
          placeholder="Type your question or what you'd like help with…"
          aria-label="Your message"
          rows={3}
          className={cn(
            'min-h-[84px] rounded-[12px] border-[1px] border-[#e6dccd] bg-[#FEFCF9] px-[15px] py-[13px]',
            'text-[14px] leading-[1.55] text-[#31364e]',
            'placeholder:text-[#b3a996]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e36001]/25',
            'disabled:opacity-50'
          )}
        />

        {error && (
          <div
            role="alert"
            className="text-[12.5px]"
            style={{ color: '#b3261e' }}
          >
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="flex items-center gap-1 text-[14px] font-semibold text-[#8a7c68] disabled:opacity-50"
          >
            <ChevronLeft aria-hidden="true" className="size-[14px]" />
            Back
          </button>
          <button
            type="button"
            onClick={() => onSubmit(value.trim())}
            disabled={!canSubmit}
            className={cn(
              'rounded-[22px] px-[18px] py-[10px] text-[13.5px] font-semibold text-white',
              'shadow-[0_10px_22px_-12px_rgba(227,96,1,0.7)]',
              canSubmit ? 'bg-[#E36001]' : 'cursor-not-allowed bg-[#E36001]/60'
            )}
          >
            {submitting ? 'Sending…' : 'Send message'}
          </button>
        </div>
      </div>
    </StandardCard>
  );
}
